<template>
  <div class="analytics-page">
    <!-- 顶部筛选栏 -->
    <div class="filter-bar">
      <div class="filter-group">
        <div class="filter-item">
          <span class="filter-label">时间范围</span>
          <div class="date-range">
            <input type="date" v-model="dateRange.start" class="date-input" />
            <span class="range-sep">至</span>
            <input type="date" v-model="dateRange.end" class="date-input" />
          </div>
        </div>
        <div class="filter-item">
          <span class="filter-label">数据维度</span>
          <select v-model="dataDimension" class="filter-select">
            <option value="daily">按日</option>
            <option value="weekly">按周</option>
            <option value="monthly">按月</option>
            <option value="quarterly">按季</option>
          </select>
        </div>
        <div class="filter-item">
          <span class="filter-label">对比周期</span>
          <select v-model="comparePeriod" class="filter-select">
            <option value="none">无对比</option>
            <option value="prev">环比</option>
            <option value="lastYear">同比</option>
          </select>
        </div>
        <div class="filter-item">
          <span class="filter-label">部门</span>
          <select v-model="filterDept" class="filter-select">
            <option value="">全部部门</option>
            <option value="sales">销售部</option>
            <option value="marketing">市场部</option>
            <option value="service">客服部</option>
          </select>
        </div>
      </div>
      <div class="action-group">
        <button class="action-btn btn-primary" @click="handleExport">
          <span class="btn-icon">↓</span>
          <span>导出报表</span>
        </button>
      </div>
    </div>

    <!-- 核心指标卡片区 -->
    <div class="core-metrics">
      <div class="metric-card" v-for="(item, index) in coreMetrics" :key="index">
        <div class="metric-header">
          <span class="metric-name">{{ item.name }}</span>
          <div class="metric-compare" v-if="comparePeriod !== 'none'">
            <span :class="item.compare > 0 ? 'up' : 'down'">
              {{ item.compare > 0 ? '↑' : '↓' }} {{ Math.abs(item.compare) }}%
            </span>
            <span class="compare-label">{{ comparePeriod === 'prev' ? '环比' : '同比' }}</span>
          </div>
        </div>
        <div class="metric-value-row">
          <span class="metric-value">{{ item.value }}</span>
          <div class="metric-trend-mini" :ref="'coreTrend' + index"></div>
        </div>
        <div class="metric-sub">
          <span>{{ item.subLabel }}: {{ item.subValue }}</span>
        </div>
      </div>
    </div>

    <!-- 主体图表区 -->
    <div class="charts-section">
      <!-- 左侧主图表 -->
      <div class="chart-main">
        <div class="panel-header">
          <div class="header-left">
            <span class="panel-title">📊 核心指标趋势分析</span>
            <div class="chart-legend">
              <span 
                v-for="legend in mainChartLegends" 
                :key="legend.key"
                :class="{ active: mainChartSeries.includes(legend.key) }"
                @click="toggleMainSeries(legend.key)"
              >
                <i :style="{ background: legend.color }"></i>
                {{ legend.label }}
              </span>
            </div>
          </div>
          <div class="header-right">
            <span class="data-tip">数据更新时间: {{ updateTime }}</span>
          </div>
        </div>
        <div class="panel-body">
          <div ref="mainChart" class="echarts-container"></div>
        </div>
      </div>

      <!-- 右侧辅助图表 -->
      <div class="chart-side">
        <div class="side-panel">
          <div class="panel-header">
            <span class="panel-title">🥧 客户构成分析</span>
          </div>
          <div class="panel-body">
            <div ref="pieChart" class="echarts-container"></div>
          </div>
        </div>
        <div class="side-panel">
          <div class="panel-header">
            <span class="panel-title">📈 渠道转化漏斗</span>
          </div>
          <div class="panel-body">
            <div ref="funnelChart" class="echarts-container"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部数据表格 -->
    <div class="data-table-section">
      <div class="table-header">
        <span class="table-title">📋 详细数据报表</span>
        <div class="table-tabs">
          <span 
            v-for="tab in tableTabs" 
            :key="tab.key"
            :class="{ active: activeTableTab === tab.key }"
            @click="activeTableTab = tab.key"
          >
            {{ tab.label }}
          </span>
        </div>
      </div>
      <div class="table-body">
        <table class="data-table">
          <thead>
            <tr>
              <th v-for="col in tableColumns" :key="col.key" :style="{ width: col.width }">
                {{ col.label }}
                <span class="sort-icon" v-if="col.sortable" @click="handleSort(col.key)">
                  {{ sortKey === col.key ? (sortOrder === 'asc' ? '↑' : '↓') : '⇅' }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in sortedTableData" :key="idx">
              <td v-for="col in tableColumns" :key="col.key">
                <span v-if="col.key === 'trend'" class="cell-trend">
                  <span :class="row[col.key] > 0 ? 'up' : 'down'">
                    {{ row[col.key] > 0 ? '↑' : '↓' }} {{ Math.abs(row[col.key]) }}%
                  </span>
                </span>
                <span v-else-if="col.key === 'status'" class="cell-status" :class="row[col.key]">
                  {{ getStatusText(row[col.key]) }}
                </span>
                <span v-else :class="{ 'highlight': col.highlight }">
                  {{ row[col.key] }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer">
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">←</button>
          <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">→</button>
        </div>
        <div class="table-summary">
          共 {{ tableData.length }} 条数据，每页 10 条
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrmAnalytics',
  data() {
    return {
      dateRange: {
        start: '2026-06-01',
        end: '2026-06-25'
      },
      dataDimension: 'daily',
      comparePeriod: 'prev',
      filterDept: '',
      updateTime: '2026-06-25 14:30',
      currentPage: 1,
      totalPages: 3,
      sortKey: '',
      sortOrder: 'desc',
      activeTableTab: 'sales',
      mainChartSeries: ['revenue', 'orders'],
      mainChartLegends: [
        { key: 'revenue', label: '营收', color: '#4CAF50' },
        { key: 'orders', label: '订单', color: '#81C784' },
        { key: 'customers', label: '新增客户', color: '#66BB6A' },
        { key: 'conversion', label: '转化率', color: '#A5D6A7' }
      ],
      tableTabs: [
        { key: 'sales', label: '销售数据' },
        { key: 'marketing', label: '营销数据' },
        { key: 'service', label: '服务数据' },
        { key: 'customer', label: '客户数据' }
      ],
      coreMetrics: [
        {
          name: '总营收',
          value: '¥486.5万',
          subLabel: '目标完成率',
          subValue: '92.3%',
          compare: 12.5,
          sparkline: [320, 332, 301, 334, 390, 430, 410, 450, 480, 520, 560, 486]
        },
        {
          name: '订单量',
          value: '1,256',
          subLabel: '客单价',
          subValue: '¥3,876',
          compare: 8.2,
          sparkline: [80, 92, 85, 98, 110, 125, 118, 130, 140, 150, 160, 156]
        },
        {
          name: '新增客户',
          value: '328',
          subLabel: '获客成本',
          subValue: '¥485',
          compare: -5.3,
          sparkline: [25, 30, 28, 35, 32, 38, 36, 40, 42, 38, 35, 32]
        },
        {
          name: '转化率',
          value: '18.6%',
          subLabel: '行业平均',
          subValue: '15.2%',
          compare: 3.4,
          sparkline: [15, 15.5, 16, 16.5, 17, 17.5, 17.8, 18, 18.2, 18.5, 18.6, 18.6]
        },
        {
          name: '客户满意度',
          value: '4.82',
          subLabel: 'NPS评分',
          subValue: '72',
          compare: 2.1,
          sparkline: [4.5, 4.55, 4.6, 4.62, 4.65, 4.7, 4.72, 4.75, 4.78, 4.8, 4.81, 4.82]
        },
        {
          name: '复购率',
          value: '68.5%',
          subLabel: '同比去年',
          subValue: '+12.3%',
          compare: 12.3,
          sparkline: [55, 57, 58, 60, 61, 63, 64, 65, 66, 67, 68, 68.5]
        }
      ],
      tableColumns: [
        { key: 'date', label: '日期', width: '120px' },
        { key: 'revenue', label: '营收(万)', width: '100px', highlight: true },
        { key: 'orders', label: '订单数', width: '90px' },
        { key: 'customers', label: '新增客户', width: '100px' },
        { key: 'conversion', label: '转化率', width: '90px' },
        { key: 'avgOrder', label: '客单价', width: '100px' },
        { key: 'trend', label: '环比增长', width: '100px' },
        { key: 'status', label: '状态', width: '80px' }
      ],
      tableData: [
        { date: '06-25', revenue: '18.5', orders: 48, customers: 12, conversion: '19.2%', avgOrder: '3,854', trend: 5.2, status: 'good' },
        { date: '06-24', revenue: '17.6', orders: 45, customers: 10, conversion: '18.8%', avgOrder: '3,911', trend: 3.1, status: 'good' },
        { date: '06-23', revenue: '17.1', orders: 43, customers: 15, conversion: '17.5%', avgOrder: '3,977', trend: -2.3, status: 'warning' },
        { date: '06-22', revenue: '17.5', orders: 46, customers: 11, conversion: '18.2%', avgOrder: '3,804', trend: 8.7, status: 'good' },
        { date: '06-21', revenue: '16.1', orders: 42, customers: 9, conversion: '16.8%', avgOrder: '3,833', trend: -1.5, status: 'normal' },
        { date: '06-20', revenue: '16.3', orders: 40, customers: 14, conversion: '17.1%', avgOrder: '4,075', trend: 4.2, status: 'good' },
        { date: '06-19', revenue: '15.7', orders: 38, customers: 8, conversion: '16.5%', avgOrder: '4,132', trend: -5.8, status: 'warning' },
        { date: '06-18', revenue: '16.6', orders: 44, customers: 13, conversion: '18.1%', avgOrder: '3,773', trend: 12.1, status: 'good' },
        { date: '06-17', revenue: '14.8', orders: 36, customers: 7, conversion: '15.2%', avgOrder: '4,111', trend: -8.3, status: 'warning' },
        { date: '06-16', revenue: '16.1', orders: 41, customers: 16, conversion: '17.8%', avgOrder: '3,927', trend: 2.5, status: 'good' }
      ],
      charts: {}
    }
  },
  computed: {
    sortedTableData() {
      let data = [...this.tableData]
      if (this.sortKey) {
        data.sort((a, b) => {
          let va = a[this.sortKey]
          let vb = b[this.sortKey]
          if (typeof va === 'string' && va.includes('%')) {
            va = parseFloat(va)
            vb = parseFloat(vb)
          }
          if (this.sortOrder === 'asc') {
            return va > vb ? 1 : -1
          } else {
            return va < vb ? 1 : -1
          }
        })
      }
      return data
    }
  },
  watch: {
    mainChartSeries: {
      handler() {
        this.$nextTick(() => this.initMainChart())
      },
      deep: true
    },
    comparePeriod() {
      this.$nextTick(() => this.initMainChart())
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCoreTrends()
      this.initMainChart()
      this.initPieChart()
      this.initFunnelChart()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    Object.values(this.charts).forEach(chart => chart && chart.dispose())
  },
  methods: {
    toggleMainSeries(key) {
      const idx = this.mainChartSeries.indexOf(key)
      if (idx > -1) {
        if (this.mainChartSeries.length > 1) {
          this.mainChartSeries.splice(idx, 1)
        }
      } else {
        this.mainChartSeries.push(key)
      }
    },
    getStatusText(status) {
      const map = { good: '优秀', warning: '预警', normal: '正常' }
      return map[status] || status
    },
    handleSort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }
    },
    handleExport() {
      alert('报表导出功能开发中...')
    },
    initCoreTrends() {
      this.coreMetrics.forEach((item, index) => {
        const dom = this.$refs['coreTrend' + index]
        if (!dom || !dom[0]) return
        const chart = this.$echarts.init(dom[0])
        const color = item.compare > 0 ? '#4CAF50' : '#EF5350'
        
        chart.setOption({
          grid: { top: 2, right: 2, bottom: 2, left: 2 },
          xAxis: { type: 'category', show: false, data: Array.from({length: 12}, (_, i) => i) },
          yAxis: { type: 'value', show: false },
          series: [{
            type: 'line',
            data: item.sparkline,
            smooth: true,
            symbol: 'none',
            lineStyle: { width: 1.5, color: color },
            areaStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: color + '30' },
                { offset: 1, color: 'transparent' }
              ])
            }
          }]
        })
        this.charts['coreTrend' + index] = chart
      })
    },
    initMainChart() {
      const chart = this.$echarts.init(this.$refs.mainChart)
      const dates = Array.from({length: 30}, (_, i) => `6.${i + 1}`)
      const seriesMap = {
        revenue: {
          name: '营收',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * 10 + 12)),
          color: '#4CAF50',
          yAxisIndex: 0
        },
        orders: {
          name: '订单',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * 20 + 30)),
          color: '#81C784',
          yAxisIndex: 0
        },
        customers: {
          name: '新增客户',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * 15 + 5)),
          color: '#66BB6A',
          yAxisIndex: 0
        },
        conversion: {
          name: '转化率',
          data: Array.from({length: 30}, () => (Math.random() * 5 + 14).toFixed(1)),
          color: '#A5D6A7',
          yAxisIndex: 1
        }
      }
      
      const series = this.mainChartSeries.map(key => ({
        name: seriesMap[key].name,
        type: key === 'conversion' ? 'line' : 'bar',
        data: seriesMap[key].data,
        yAxisIndex: seriesMap[key].yAxisIndex,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { width: 2, color: seriesMap[key].color },
        itemStyle: { color: seriesMap[key].color },
        barWidth: key === 'revenue' ? 12 : 8,
        areaStyle: key === 'conversion' ? {
          color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: seriesMap[key].color + '30' },
            { offset: 1, color: 'transparent' }
          ])
        } : undefined
      }))

      // 对比线
      if (this.comparePeriod !== 'none') {
        series.push({
          name: this.comparePeriod === 'prev' ? '环比' : '同比',
          type: 'line',
          data: seriesMap.revenue.data.map(v => v * (0.85 + Math.random() * 0.3)),
          yAxisIndex: 0,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color: '#66BB6A', type: 'dashed' },
          itemStyle: { color: '#66BB6A' }
        })
      }

      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(16, 42, 21, 0.95)',
          borderColor: 'rgba(76, 175, 80, 0.3)',
          textStyle: { color: '#e8f5e9' },
          axisPointer: { type: 'cross', crossStyle: { color: 'rgba(76, 175, 80, 0.3)' } }
        },
        legend: { show: false },
        grid: { left: 50, right: 50, top: 20, bottom: 30 },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: { lineStyle: { color: 'rgba(76, 175, 80, 0.2)' } },
          axisLabel: { color: '#a5d6a7', fontSize: 10, interval: 4 }
        },
        yAxis: [
          {
            type: 'value',
            axisLine: { show: false },
            axisLabel: { color: '#a5d6a7', fontSize: 10 },
            splitLine: { lineStyle: { color: 'rgba(76, 175, 80, 0.06)' } }
          },
          {
            type: 'value',
            axisLine: { show: false },
            axisLabel: { color: '#a5d6a7', fontSize: 10, formatter: '{value}%' },
            splitLine: { show: false }
          }
        ],
        series
      })
      this.charts.main = chart
    },
    initPieChart() {
      const chart = this.$echarts.init(this.$refs.pieChart)
      chart.setOption({
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(16, 42, 21, 0.95)',
          borderColor: 'rgba(76, 175, 80, 0.3)',
          textStyle: { color: '#e8f5e9' },
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'horizontal',
          bottom: 5,
          textStyle: { color: '#a5d6a7', fontSize: 10 },
          itemWidth: 10,
          itemHeight: 10
        },
        series: [{
          type: 'pie',
          radius: ['35%', '55%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: false,
          itemStyle: { borderRadius: 4, borderColor: '#0d1f0f', borderWidth: 2 },
          label: { show: false },
          emphasis: {
            label: { show: true, fontSize: 12, fontWeight: 'bold', color: '#e8f5e9', formatter: '{b}\n{d}%' }
          },
          data: [
            { value: 4350, name: '企业客户', itemStyle: { color: '#4CAF50' } },
            { value: 3100, name: '个人客户', itemStyle: { color: '#66BB6A' } },
            { value: 2340, name: '代理商', itemStyle: { color: '#81C784' } },
            { value: 1800, name: '合作伙伴', itemStyle: { color: '#A5D6A7' } },
            { value: 1257, name: '其他', itemStyle: { color: '#2E7D32' } }
          ]
        }]
      })
      this.charts.pie = chart
    },
    initFunnelChart() {
      const chart = this.$echarts.init(this.$refs.funnelChart)
      chart.setOption({
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(16, 42, 21, 0.95)',
          borderColor: 'rgba(76, 175, 80, 0.3)',
          textStyle: { color: '#e8f5e9' },
          formatter: '{b}: {c}%'
        },
        series: [{
          type: 'funnel',
          left: '15%',
          top: 10,
          bottom: 10,
          width: '70%',
          min: 0,
          max: 100,
          minSize: '0%',
          maxSize: '100%',
          sort: 'descending',
          gap: 2,
          label: {
            show: true,
            position: 'inside',
            color: '#fff',
            fontSize: 11,
            formatter: '{b}\n{c}%'
          },
          itemStyle: { borderColor: '#0d1f0f', borderWidth: 1 },
          data: [
            { value: 100, name: '访问', itemStyle: { color: '#1B5E20' } },
            { value: 68, name: '注册', itemStyle: { color: '#2E7D32' } },
            { value: 45, name: '试用', itemStyle: { color: '#388E3C' } },
            { value: 28, name: '付费', itemStyle: { color: '#4CAF50' } },
            { value: 18, name: '复购', itemStyle: { color: '#66BB6A' } }
          ]
        }]
      })
      this.charts.funnel = chart
    },
    handleResize() {
      Object.values(this.charts).forEach(chart => {
        chart && chart.resize()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.analytics-page {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  background: #0a1f0f;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(76, 175, 80, 0.2);
    border-radius: 3px;
  }
}

// ===== 筛选栏 =====
.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;

  .filter-group {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .action-group {
    display: flex;
    gap: 10px;
  }
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;

  .filter-label {
    font-size: 12px;
    color: #66BB6A;
    white-space: nowrap;
  }

  .date-range {
    display: flex;
    align-items: center;
    gap: 6px;

    .date-input {
      padding: 6px 10px;
      background: rgba(16, 42, 21, 0.8);
      border: 1px solid rgba(76, 175, 80, 0.2);
      border-radius: 6px;
      color: #e8f5e9;
      font-size: 12px;
      outline: none;
      font-family: 'Courier New', monospace;

      &::-webkit-calendar-picker-indicator {
        filter: invert(0.6) sepia(1) saturate(3) hue-rotate(80deg);
        cursor: pointer;
      }
    }

    .range-sep {
      color: #66BB6A;
      font-size: 12px;
    }
  }

  .filter-select {
    padding: 6px 10px;
    background: rgba(16, 42, 21, 0.8);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #a5d6a7;
    font-size: 12px;
    outline: none;
    cursor: pointer;

    &:focus {
      border-color: #4CAF50;
    }

    option {
      background: #102A15;
    }
  }
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;

  .btn-icon {
    font-size: 14px;
    font-weight: 600;
  }

  &.btn-primary {
    background: linear-gradient(135deg, #2E7D32, #1B5E20);
    color: #e8f5e9;
    box-shadow: 0 2px 10px rgba(46, 125, 50, 0.3);

    &:hover {
      background: linear-gradient(135deg, #388E3C, #2E7D32);
      box-shadow: 0 4px 16px rgba(46, 125, 50, 0.4);
      transform: translateY(-1px);
    }
  }
}

// ===== 核心指标卡片 =====
.core-metrics {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 14px;
  margin-bottom: 16px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.metric-card {
  padding: 16px;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(76, 175, 80, 0.3);
    transform: translateY(-2px);
  }

  .metric-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    .metric-name {
      font-size: 12px;
      color: #66BB6A;
      opacity: 0.9;
    }

    .metric-compare {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      font-weight: 500;

      span {
        &.up { color: #4CAF50; }
        &.down { color: #EF5350; }
      }

      .compare-label {
        color: #66BB6A;
        opacity: 0.6;
      }
    }
  }

  .metric-value-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .metric-value {
      font-size: 22px;
      font-weight: 700;
      color: #e8f5e9;
      font-family: 'Courier New', monospace;
    }

    .metric-trend-mini {
      width: 60px;
      height: 30px;
    }
  }

  .metric-sub {
    font-size: 11px;
    color: #4CAF50;
    opacity: 0.7;

    span {
      font-family: 'Courier New', monospace;
    }
  }
}

// ===== 图表主体区 =====
.charts-section {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
}

.chart-main {
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(76, 175, 80, 0.08);

    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;

      .panel-title {
        font-size: 15px;
        font-weight: 600;
        color: #e8f5e9;
      }

      .chart-legend {
        display: flex;
        gap: 12px;

        span {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #66BB6A;
          cursor: pointer;
          transition: all 0.2s;
          opacity: 0.5;

          &.active {
            opacity: 1;
            color: #a5d6a7;
          }

          i {
            width: 8px;
            height: 8px;
            border-radius: 2px;
          }
        }
      }
    }

    .header-right {
      .data-tip {
        font-size: 11px;
        color: #66BB6A;
        opacity: 0.6;
        font-family: 'Courier New', monospace;
      }
    }
  }

  .panel-body {
    flex: 1;
    padding: 12px;
    min-height: 360px;
  }
}

.chart-side {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .side-panel {
    flex: 1;
    background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
    border: 1px solid rgba(76, 175, 80, 0.12);
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .panel-header {
      padding: 12px 16px;
      border-bottom: 1px solid rgba(76, 175, 80, 0.08);

      .panel-title {
        font-size: 14px;
        font-weight: 600;
        color: #e8f5e9;
      }
    }

    .panel-body {
      flex: 1;
      padding: 10px;
      min-height: 160px;
    }
  }
}

.echarts-container {
  width: 100%;
  height: 100%;
  min-height: inherit;
}

// ===== 数据表格区 =====
.data-table-section {
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(76, 175, 80, 0.08);

  .table-title {
    font-size: 15px;
    font-weight: 600;
    color: #e8f5e9;
  }

  .table-tabs {
    display: flex;
    gap: 4px;

    span {
      padding: 5px 14px;
      border-radius: 6px;
      font-size: 12px;
      color: #66BB6A;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(76, 175, 80, 0.08);
      }

      &.active {
        background: rgba(76, 175, 80, 0.15);
        color: #e8f5e9;
        font-weight: 500;
      }
    }
  }
}

.table-body {
  overflow-x: auto;
  padding: 0 4px;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(76, 175, 80, 0.2);
    border-radius: 2px;
  }
}

.data-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    tr {
      border-bottom: 1px solid rgba(76, 175, 80, 0.1);
    }

    th {
      padding: 12px 14px;
      text-align: left;
      font-size: 12px;
      font-weight: 600;
      color: #81C784;
      white-space: nowrap;
      cursor: default;

      .sort-icon {
        margin-left: 4px;
        cursor: pointer;
        opacity: 0.5;
        transition: opacity 0.2s;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid rgba(76, 175, 80, 0.05);
      transition: background 0.2s;

      &:hover {
        background: rgba(76, 175, 80, 0.04);
      }

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 12px 14px;
      font-size: 13px;
      color: #a5d6a7;
      white-space: nowrap;

      .highlight {
        font-weight: 600;
        color: #e8f5e9;
        font-family: 'Courier New', monospace;
      }

      .cell-trend {
        span {
          font-weight: 500;
          font-size: 12px;

          &.up { color: #4CAF50; }
          &.down { color: #EF5350; }
        }
      }

      .cell-status {
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 500;

        &.good {
          color: #4CAF50;
          background: rgba(76, 175, 80, 0.1);
        }
        &.warning {
          color: #EF5350;
          background: rgba(239, 83, 80, 0.1);
        }
        &.normal {
          color: #66BB6A;
          background: rgba(102, 187, 106, 0.1);
        }
      }
    }
  }
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-top: 1px solid rgba(76, 175, 80, 0.08);

  .pagination {
    display: flex;
    align-items: center;
    gap: 12px;

    .page-btn {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(76, 175, 80, 0.08);
      border: 1px solid rgba(76, 175, 80, 0.15);
      border-radius: 6px;
      color: #a5d6a7;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover:not(:disabled) {
        background: rgba(76, 175, 80, 0.15);
        border-color: rgba(76, 175, 80, 0.3);
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }

    .page-info {
      font-size: 12px;
      color: #66BB6A;
    }
  }

  .table-summary {
    font-size: 11px;
    color: #66BB6A;
    opacity: 0.6;
  }
}

// ===== 响应式 =====
@media (max-width: 768px) {
  .filter-bar {
    .filter-group {
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
    }
  }

  .core-metrics {
    grid-template-columns: 1fr;
  }

  .charts-section {
    grid-template-columns: 1fr;
  }

  .chart-main .panel-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>